import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

/** 3열(왼쪽·가운데·오른쪽) 가로 리사이즈 기본값 */
export const RESIZABLE_THREE_COLUMNS = {
  handleWidthPx: 6,
  minLeft: 160,
  minRight: 220,
  minCenter: 280,
  defaultLeft: 250,
  defaultRight: 350,
  maxLeftPercent: 50,
  maxRightPercent: 45,
};

/**
 * 왼쪽·오른쪽 고정 너비 열 + 가운데 flex 열 레이아웃의 리사이즈 상태/핸들러
 *
 * @param {Object} [options]
 * @param {number} [options.handleWidthPx]
 * @param {number} [options.minLeft]
 * @param {number} [options.minRight]
 * @param {number} [options.minCenter]
 * @param {number} [options.defaultLeft]
 * @param {number} [options.defaultRight]
 */
export const useResizableThreeColumns = (options = {}) => {
  const {
    handleWidthPx = RESIZABLE_THREE_COLUMNS.handleWidthPx,
    minLeft = RESIZABLE_THREE_COLUMNS.minLeft,
    minRight = RESIZABLE_THREE_COLUMNS.minRight,
    minCenter = RESIZABLE_THREE_COLUMNS.minCenter,
    defaultLeft = RESIZABLE_THREE_COLUMNS.defaultLeft,
    defaultRight = RESIZABLE_THREE_COLUMNS.defaultRight,
  } = options;

  const containerRef = useRef(null);
  const [leftWidth, setLeftWidth] = useState(defaultLeft);
  const [rightWidth, setRightWidth] = useState(defaultRight);
  const dragRef = useRef({ type: null, startX: 0, startLeft: 0, startRight: 0 });

  const startResizeLeft = useCallback(
    (e) => {
      e.preventDefault();
      dragRef.current = {
        type: 'left',
        startX: e.clientX,
        startLeft: leftWidth,
        startRight: rightWidth,
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [leftWidth, rightWidth]
  );

  const startResizeRight = useCallback(
    (e) => {
      e.preventDefault();
      dragRef.current = {
        type: 'right',
        startX: e.clientX,
        startLeft: leftWidth,
        startRight: rightWidth,
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [leftWidth, rightWidth]
  );

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag.type) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const handles = handleWidthPx * 2;
      if (drag.type === 'left') {
        const maxLeft = Math.max(
          minLeft,
          rect.width - handles - minCenter - drag.startRight
        );
        const next = Math.min(
          maxLeft,
          Math.max(minLeft, drag.startLeft + (e.clientX - drag.startX))
        );
        setLeftWidth(next);
      } else if (drag.type === 'right') {
        const maxRight = Math.max(
          minRight,
          rect.width - handles - minCenter - drag.startLeft
        );
        const next = Math.min(
          maxRight,
          Math.max(minRight, drag.startRight + (drag.startX - e.clientX))
        );
        setRightWidth(next);
      }
    };
    const onUp = () => {
      if (!dragRef.current.type) return;
      dragRef.current = { type: null, startX: 0, startLeft: 0, startRight: 0 };
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [handleWidthPx, minLeft, minRight, minCenter]);

  return {
    containerRef,
    leftWidth,
    rightWidth,
    startResizeLeft,
    startResizeRight,
    handleWidthPx,
    minLeft,
    minRight,
    minCenter,
  };
};

const handleSx = {
  flexShrink: 0,
  cursor: 'col-resize',
  alignSelf: 'stretch',
  bgcolor: 'grey.200',
  '&:hover': { bgcolor: 'primary.light' },
  transition: 'background-color 0.15s',
  touchAction: 'none',
};

/**
 * 3열 레이아웃용 세로 리사이즈 핸들
 *
 * @param {Object} props
 * @param {React.MouseEventHandler} props.onMouseDown
 * @param {string} props.ariaLabel
 * @param {number} props.handleWidthPx
 */
export const ResizeColumnHandle = ({ onMouseDown, ariaLabel, handleWidthPx }) => (
  <Box
    role="separator"
    aria-orientation="vertical"
    aria-label={ariaLabel}
    onMouseDown={onMouseDown}
    sx={{
      ...handleSx,
      width: handleWidthPx,
    }}
  />
);
